import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  Leaf, LayoutDashboard, Box, FileText, ShoppingBag, 
  ListChecks, Settings, Mail, Upload, LogOut, Menu, X,
  ChevronRight, Layers, Target, BarChart3, ClipboardList, Cog, Puzzle, Sparkles, Code, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loadAdminCredentials, setAdminCredentials, clearAdminCredentials, adminApi } from '@/lib/api';
import { toast } from 'sonner';

const AdminLayout = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [checking, setChecking] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    if (loadAdminCredentials()) {
      try {
        await adminApi.get('/stats');
        setAuthenticated(true);
      } catch (error) {
        clearAdminCredentials();
      }
    }
    setChecking(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAdminCredentials(password);
    try {
      await adminApi.get('/stats');
      setAuthenticated(true);
      toast.success('Logged in successfully');
    } catch (error) {
      clearAdminCredentials();
      toast.error('Invalid password');
    }
  };

  const handleLogout = () => {
    clearAdminCredentials();
    setAuthenticated(false);
    setPassword('');
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/responses', icon: ClipboardList, label: 'Quiz Responses' },
    { path: '/admin/systems', icon: Layers, label: 'Systems' },
    { path: '/admin/conditions', icon: Target, label: 'Conditions' },
    { path: '/admin/products', icon: ShoppingBag, label: 'Products' },
    { path: '/admin/protocols', icon: Box, label: 'Protocols' },
    { path: '/admin/protocol-builder', icon: Puzzle, label: 'Protocol Builder' },
    { path: '/admin/cross-sell', icon: Sparkles, label: 'Cross-Sell' },
    { path: '/admin/quiz-questions', icon: ListChecks, label: 'Quiz Questions' },
    { path: '/admin/answer-rules', icon: Settings, label: 'Scoring Rules' },
    { path: '/admin/result-templates', icon: FileText, label: 'Result Templates' },
    { path: '/admin/emails', icon: Mail, label: 'Email Captures' },
    { path: '/admin/import', icon: Upload, label: 'Import / Export' },
    { path: '/admin/embed-guide', icon: Code, label: 'Embed Guide' },
    { path: '/admin/documentation', icon: BookOpen, label: 'Documentation' },
    { path: '/admin/settings', icon: Cog, label: 'Settings' },
  ];

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Leaf className="w-10 h-10 text-[#4A6763] animate-pulse" />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="max-w-md w-full px-6">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#4A6763]/10 flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-[#4A6763]" strokeWidth={1.5} />
              </div>
              <h1 className="font-serif text-2xl text-[#2C2C2C] mb-2">Admin Dashboard</h1>
              <p className="text-stone-600 text-sm">Enter your password to continue</p>
            </div>
            <form onSubmit={handleLogin}>
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mb-4 h-12"
                data-testid="admin-password-input"
              />
              <Button 
                type="submit" 
                className="w-full btn-primary"
                data-testid="admin-login-btn"
              >
                Login
              </Button>
            </form>
            <div className="mt-6 text-center">
              <a 
                href="/" 
                className="text-sm text-stone-500 hover:text-[#4A6763]"
              >
                Back to Website
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-stone-100
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Leaf className="w-6 h-6 text-[#4A6763]" strokeWidth={1.5} />
                <span className="font-serif text-lg text-[#2C2C2C]">Admin</span>
              </div>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-stone-400 hover:text-stone-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.exact}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `
                      admin-nav-item
                      ${isActive ? 'active' : ''}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    <span>{item.label}</span>
                    <ChevronRight className="w-4 h-4 ml-auto opacity-50" strokeWidth={1.5} />
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-stone-100">
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-stone-600 hover:text-red-600 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
              Logout
            </Button>
            <a 
              href="/" 
              target="_blank"
              className="flex items-center gap-3 px-4 py-3 mt-2 text-sm text-stone-500 hover:text-[#4A6763] rounded-lg hover:bg-stone-50"
            >
              <Leaf className="w-5 h-5" strokeWidth={1.5} />
              View Website
            </a>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-stone-100 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-stone-600 hover:text-[#4A6763]"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
              <span className="font-serif text-lg text-[#2C2C2C]">Admin</span>
            </div>
            <div className="w-6" /> {/* Spacer */}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
